import { useEffect, useState } from "react";
import { api, API, formatApiError, fmtINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, MagnifyingGlass, Pencil, Receipt, Download } from "@phosphor-icons/react";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";
import SortableHeader from "@/components/SortableHeader";

const emptyForm = {
  name: "", gstin: "", pan: "", contact: "", email: "",
  address: "", bank_account: "", ifsc: "", category: "", notes: "",
};
const PAGE_SIZE = 10;

export default function Vendors() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [ledgerVendor, setLedgerVendor] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [ledgerBusy, setLedgerBusy] = useState(false);
  const [ledgerRange, setLedgerRange] = useState({ start_date: "", end_date: "" });

  const openLedger = async (v, range = { start_date: "", end_date: "" }) => {
    setLedgerVendor(v);
    setLedgerRange(range);
    setLedger(null);
    setLedgerBusy(true);
    try {
      const r = await api.get(`/vendors/${v.id}/ledger`, { params: range });
      setLedger(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load ledger");
      setLedgerVendor(null);
    } finally {
      setLedgerBusy(false);
    }
  };
  const [f, setF] = useState(emptyForm);
  const canCreate = ["admin", "accounts", "finance", "ho_approver", "centre_manager", "centre_coordinator"].includes(user?.role);
  const canEdit = canCreate;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => { setPage(1); }, [debouncedQ]);

  const load = () => {
    const params = { page, page_size: PAGE_SIZE };
    if (debouncedQ) params.q = debouncedQ;
    if (sortBy && sortDir) { params.sort_by = sortBy; params.sort_dir = sortDir; }
    return api.get("/vendors", { params }).then((r) => {
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    });
  };
  useEffect(() => { load(); }, [page, debouncedQ, sortBy, sortDir]); // eslint-disable-line

  const applySort = (field, dir) => { setSortBy(field); setSortDir(dir); setPage(1); };
  const hdrProps = { sortBy, sortDir, onChange: applySort };

  const openCreate = () => { setEditing(null); setF(emptyForm); setOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    setF({
      name: v.name || "", gstin: v.gstin || "", pan: v.pan || "",
      contact: v.contact || "", email: v.email || "", address: v.address || "",
      bank_account: v.bank_account || "", ifsc: v.ifsc || "",
      category: v.category || "", notes: v.notes || "",
    });
    setOpen(true);
  };
  const submit = async () => {
    if (!f.name.trim()) return toast.error("Vendor name required");
    try {
      const payload = { ...f, gstin: f.gstin?.toUpperCase() };
      if (editing) {
        await api.patch(`/vendors/${editing.id}`, payload);
        toast.success(`Vendor "${f.name}" updated`);
      } else {
        await api.post("/vendors", payload);
        toast.success("Vendor created");
      }
      setOpen(false);
      setEditing(null);
      setF(emptyForm);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="vendors-root">
      <div className="flex justify-between items-end">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Master</div>
          <h1 className="font-heading text-4xl mt-1 tracking-tight">Vendors</h1>
          <p className="text-sm text-zinc-500 mt-1">Registered suppliers with GSTIN & bank details for faster, duplicate-free expense entry.</p>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-10 gap-2" data-testid="new-vendor-btn">
                <Plus size={14} weight="bold" /> New Vendor
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white rounded-sm max-w-2xl">
              <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Vendor" : "Create Vendor"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <F label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-sm h-10 mt-2" data-testid="vendor-name" /></F>
                <F label="Category"><Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="rounded-sm h-10 mt-2" /></F>
                <F label="GSTIN"><Input value={f.gstin} onChange={(e) => setF({ ...f, gstin: e.target.value.toUpperCase() })} className="rounded-sm h-10 mt-2 font-mono" data-testid="vendor-gstin" /></F>
                <F label="PAN"><Input value={f.pan} onChange={(e) => setF({ ...f, pan: e.target.value.toUpperCase() })} className="rounded-sm h-10 mt-2 font-mono" /></F>
                <F label="Contact"><Input value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} className="rounded-sm h-10 mt-2" /></F>
                <F label="Email"><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="rounded-sm h-10 mt-2" /></F>
                <F label="Bank Account"><Input value={f.bank_account} onChange={(e) => setF({ ...f, bank_account: e.target.value })} className="rounded-sm h-10 mt-2 font-mono" /></F>
                <F label="IFSC"><Input value={f.ifsc} onChange={(e) => setF({ ...f, ifsc: e.target.value.toUpperCase() })} className="rounded-sm h-10 mt-2 font-mono" /></F>
                <div className="col-span-2"><Label className="text-xs uppercase tracking-widest">Address</Label>
                  <Textarea value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} rows={2} className="rounded-sm mt-2" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={submit} data-testid="save-vendor" className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex gap-3 items-center bg-white border border-zinc-200 p-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-3 text-zinc-400" />
          <Input placeholder="Search name, GSTIN, category…" value={q} onChange={(e) => setQ(e.target.value)}
                 className="pl-9 rounded-sm h-9 border-zinc-300" data-testid="vendor-search" />
        </div>
        <div className="text-xs text-zinc-500">{total} vendor{total === 1 ? "" : "s"}</div>
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left p-4"><SortableHeader field="name" label="Name" {...hdrProps} /></th>
              <th className="text-left p-4"><SortableHeader field="category" label="Category" {...hdrProps} /></th>
              <th className="text-left p-4"><SortableHeader field="gstin" label="GSTIN" {...hdrProps} /></th>
              <th className="text-left p-4">PAN</th>
              <th className="text-left p-4">Contact</th>
              <th className="text-left p-4">Bank</th>
              <th className="text-left p-4">Status</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-zinc-400">No vendors found.</td></tr>}
            {items.map((v) => (
              <tr key={v.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`vendor-row-${v.name.replace(/\s/g,"-").toLowerCase()}`}>
                <td className="p-4 font-medium">{v.name}</td>
                <td className="p-4 text-zinc-600">{v.category || "—"}</td>
                <td className="p-4 font-mono text-xs">{v.gstin || "—"}</td>
                <td className="p-4 font-mono text-xs">{v.pan || "—"}</td>
                <td className="p-4 text-zinc-600">{v.contact || "—"}</td>
                <td className="p-4 font-mono text-xs">{v.bank_account ? `${v.bank_account} · ${v.ifsc || ""}` : "—"}</td>
                <td className="p-4"><span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest border ${v.is_active !== false ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-zinc-100 text-zinc-600 border-zinc-300"}`}>{v.is_active !== false ? "Active" : "Inactive"}</span></td>
                <td className="p-4 text-right">
                  <div className="inline-flex gap-3 items-center">
                    <button onClick={() => openLedger(v)}
                      className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900"
                      data-testid={`ledger-vendor-${v.name.replace(/\s/g,"-").toLowerCase()}`}>
                      <Receipt size={12} /> Ledger
                    </button>
                    {canEdit && (
                      <button onClick={() => openEdit(v)}
                        className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900"
                        data-testid={`edit-vendor-${v.name.replace(/\s/g,"-").toLowerCase()}`}>
                        <Pencil size={12} /> Edit
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination page={page} pageSize={PAGE_SIZE} total={total}
          onPageChange={setPage} testid="vendors-pagination" />
      </div>

      {/* Vendor Ledger dialog */}
      <Dialog open={!!ledgerVendor} onOpenChange={(o) => !o && setLedgerVendor(null)}>
        <DialogContent className="bg-white rounded-sm max-w-5xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-hidden flex flex-col" data-testid="vendor-ledger-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Ledger · {ledgerVendor?.name}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Invoices booked (credit) and payments made (debit) with running balance.
              {ledgerVendor?.gstin && <> · GSTIN {ledgerVendor.gstin}</>}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-end gap-3 pb-3 border-b border-zinc-200">
            <div>
              <Label className="text-xs uppercase tracking-widest">From</Label>
              <Input type="date" value={ledgerRange.start_date}
                onChange={(e) => setLedgerRange({ ...ledgerRange, start_date: e.target.value })}
                className="rounded-sm h-9 mt-1" data-testid="ledger-start-date" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">To</Label>
              <Input type="date" value={ledgerRange.end_date}
                onChange={(e) => setLedgerRange({ ...ledgerRange, end_date: e.target.value })}
                className="rounded-sm h-9 mt-1" data-testid="ledger-end-date" />
            </div>
            <Button onClick={() => openLedger(ledgerVendor, ledgerRange)}
              className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-9"
              data-testid="ledger-apply">
              Apply
            </Button>
            <a className="ml-auto"
              href={`${API}/reports/vendor-ledger.xlsx?vendor_id=${ledgerVendor?.id}${ledgerRange.start_date ? `&start_date=${ledgerRange.start_date}` : ""}${ledgerRange.end_date ? `&end_date=${ledgerRange.end_date}` : ""}`}
              target="_blank" rel="noreferrer">
              <Button className="rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white h-9 gap-2"
                data-testid="ledger-download">
                <Download size={14} /> Download Excel
              </Button>
            </a>
          </div>

          <div className="flex-1 overflow-y-auto">
            {ledgerBusy && (
              <div className="p-10 text-center text-sm text-zinc-500">Loading ledger…</div>
            )}
            {!ledgerBusy && ledger && (
              <>
                <table className="w-full text-sm tabular-nums">
                  <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500 sticky top-0">
                    <tr>
                      <th className="text-left p-3">Date</th>
                      <th className="text-left p-3">Type</th>
                      <th className="text-left p-3">Ref</th>
                      <th className="text-left p-3">Centre</th>
                      <th className="text-left p-3">Description</th>
                      <th className="text-right p-3">Debit (Paid)</th>
                      <th className="text-right p-3">Credit (Billed)</th>
                      <th className="text-right p-3">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.rows.length === 0 && (
                      <tr><td colSpan={8} className="p-8 text-center text-zinc-400"
                        data-testid="ledger-empty">No transactions for this vendor in the selected range.</td></tr>
                    )}
                    {ledger.rows.map((r, i) => (
                      <tr key={`${r.ref}-${r.kind}-${i}`}
                        className={`border-t border-zinc-100 ${r.kind === "payment" ? "bg-emerald-50/40" : ""}`}
                        data-testid={`ledger-row-${r.ref}-${r.kind}`}>
                        <td className="p-3 text-zinc-600 whitespace-nowrap">{r.date || "—"}</td>
                        <td className="p-3 uppercase text-[10px] tracking-widest text-zinc-500">{r.kind}</td>
                        <td className="p-3 font-mono text-xs">{r.ref}</td>
                        <td className="p-3 text-zinc-600 font-mono text-xs">{r.centre || "—"}</td>
                        <td className="p-3 text-zinc-700 max-w-md truncate" title={r.description}>{r.description || "—"}</td>
                        <td className="p-3 text-right">{r.debit ? fmtINR(r.debit) : "—"}</td>
                        <td className="p-3 text-right">{r.credit ? fmtINR(r.credit) : "—"}</td>
                        <td className={`p-3 text-right font-medium ${r.balance >= 0 ? "text-rose-700" : "text-emerald-700"}`}>
                          {fmtINR(r.balance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {ledger.rows.length > 0 && (
                    <tfoot>
                      <tr className="border-t-2 border-zinc-300 bg-zinc-50 font-medium">
                        <td className="p-3" colSpan={5}>Total</td>
                        <td className="p-3 text-right" data-testid="ledger-total-debit">{fmtINR(ledger.total_debit)}</td>
                        <td className="p-3 text-right" data-testid="ledger-total-credit">{fmtINR(ledger.total_credit)}</td>
                        <td className={`p-3 text-right ${ledger.closing_balance > 0 ? "text-rose-700" : "text-emerald-700"}`}
                          data-testid="ledger-closing-balance">
                          {fmtINR(ledger.closing_balance)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
                {ledger.rows.length > 0 && (
                  <div className="p-3 text-[11px] text-zinc-500">
                    Positive balance = outstanding payable. Negative balance = overpaid / advance.
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setLedgerVendor(null)} className="rounded-sm" data-testid="ledger-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, children }) => (
  <div><Label className="text-xs uppercase tracking-widest">{label}</Label>{children}</div>
);
